/**
 * useSunTimes + useThemeMode — TanStack Query hooks for DuskTheme.
 *
 * Plan 01-14 Task 4.
 *
 * `useSunTimes()` polls GET /api/sun/today every 15 minutes. The endpoint
 * (apps/api/src/services/sun.service.ts) returns a SunView with a Pi-side
 * `stale: true` flag when Met.no is unreachable (D-06 fallback).
 *
 * `useThemeMode()` polls GET /api/app-config/theme-mode (Task 5) — returns
 * `{ mode: 'auto' | 'alltid lys' | 'alltid mørk' }` (Norwegian Bokmål per
 * D-06 admin UI).  Default `auto` while the first fetch is in flight.
 */
import { useQuery } from '@tanstack/react-query';

/**
 * GET /api/sun/today response shape — must match
 * apps/api/src/services/sun.service.ts SunView export.
 *
 * `stale: true` indicates the D-06 fallback engaged (Met.no unreachable).
 * On the fallback path, `sunset_at` carries a synthesised 17:00 Europe/Oslo
 * ISO string and `sunrise_at` / `solar_noon_at` are null.
 *
 * CR-03 fix (plan 01-21): was previously `{date, sunrise, sunset, fallback}` —
 * a wire-shape mismatch that made the auto branch dead.
 */
export type SunResponse = {
  sunrise_at: string | null;
  sunset_at: string | null;
  solar_noon_at: string | null;
  stale: boolean;
};

export type ThemeMode = 'auto' | 'alltid lys' | 'alltid mørk';

export type ThemeModeResponse = { mode: ThemeMode };

export function useSunTimes() {
  return useQuery<SunResponse>({
    queryKey: ['sun', 'today'],
    queryFn: async () => {
      const r = await fetch('/api/sun/today', {
        credentials: 'same-origin',
      });
      if (!r.ok) throw new Error(`sun_fetch_failed: ${r.status}`);
      return (await r.json()) as SunResponse;
    },
    staleTime: 15 * 60_000,
    refetchInterval: 15 * 60_000,
    retry: 1,
  });
}

const DEFAULT_THEME_MODE: ThemeModeResponse = { mode: 'auto' };

export function useThemeMode() {
  return useQuery<ThemeModeResponse>({
    queryKey: ['app-config', 'theme-mode'],
    queryFn: async () => {
      const r = await fetch('/api/app-config/theme-mode', {
        credentials: 'same-origin',
      });
      if (!r.ok) throw new Error(`theme_mode_fetch_failed: ${r.status}`);
      return (await r.json()) as ThemeModeResponse;
    },
    staleTime: 5 * 60_000,
    retry: 1,
    placeholderData: DEFAULT_THEME_MODE,
  });
}
